import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { RecipeV2Detail, DietaryAttribute, DietaryAttributeReference, DietaryAttributeStatus, DietaryCategory } from '../types';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';
import DietaryAttributeChips from './DietaryAttributeChips';
import RecipeV2EmptyState from './RecipeV2EmptyState';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import RecipeOverrideRemovalDialog from './RecipeOverrideRemovalDialog';

/** Categories supported by the "Add dietary attribute" flow. */
const ADDABLE_CATEGORIES = [
  DietaryCategory.DietaryRequirement,
  DietaryCategory.LifestylePreference,
  DietaryCategory.NutritionalClaim,
] as const;

const CATEGORY_LABEL_KEYS: Record<DietaryCategory, string> = {
  [DietaryCategory.Allergen]: 'dietary.categories.allergen',
  [DietaryCategory.Intolerance]: 'dietary.categories.intolerance',
  [DietaryCategory.DietaryRequirement]: 'dietary.categories.dietaryRequirement',
  [DietaryCategory.LifestylePreference]: 'dietary.categories.lifestylePreference',
  [DietaryCategory.NutritionalClaim]: 'dietary.categories.nutritionalClaim',
};

/** Status options allowed per category. */
const CATEGORY_STATUS_OPTIONS: Record<string, DietaryAttributeStatus[]> = {
  [DietaryCategory.DietaryRequirement]: [DietaryAttributeStatus.Suitable, DietaryAttributeStatus.NotSuitable, DietaryAttributeStatus.Certified],
  [DietaryCategory.LifestylePreference]: [DietaryAttributeStatus.Suitable, DietaryAttributeStatus.NotSuitable],
  [DietaryCategory.NutritionalClaim]: [DietaryAttributeStatus.Suitable, DietaryAttributeStatus.Certified],
};

const STATUS_LABEL_KEYS: Record<DietaryAttributeStatus, string> = {
  [DietaryAttributeStatus.Contains]: 'dietary.contains',
  [DietaryAttributeStatus.MayContain]: 'dietary.mayContain',
  [DietaryAttributeStatus.FreeFrom]: 'dietary.freeFrom',
  [DietaryAttributeStatus.Suitable]: 'dietary.suitable',
  [DietaryAttributeStatus.NotSuitable]: 'dietary.notSuitable',
  [DietaryAttributeStatus.Certified]: 'dietary.certified',
  [DietaryAttributeStatus.Unknown]: 'dietary.unknown',
};

const ALL_STATUS_OPTIONS = Object.values(DietaryAttributeStatus);

interface DietaryTabProps {
  recipe: RecipeV2Detail;
  onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void;
}

const displayValue = (value: string) => value.replace(/([a-z])([A-Z])/g, '$1 $2');

const statusChipColor = (status: string): 'success' | 'warning' | 'default' =>
  status === 'FreeFrom' || status === 'Suitable' || status === 'Certified'
    ? 'success'
    : status === 'MayContain' ? 'warning' : 'default';

const DietaryTab = ({ recipe, onMetadataChange }: DietaryTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canOverrideDietary = hasPermissionTo(['RecipeV2ComplianceOverride']);
  const [attributes, setAttributes] = useState<DietaryAttribute[]>(recipe.dietaryAttributes ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [reference, setReference] = useState<DietaryAttributeReference[]>([]);
  const [selected, setSelected] = useState<DietaryAttribute | DietaryAttributeReference | null>(null);
  const [status, setStatus] = useState<DietaryAttributeStatus>(DietaryAttributeStatus.Suitable);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<DietaryAttribute | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeReason, setRemoveReason] = useState('');

  useEffect(() => {
    setIsLoading(true);
    api.getDietaryAttributes(recipe.id)
      .then(setAttributes)
      .catch(() => setAttributes([]))
      .finally(() => setIsLoading(false));
  }, [recipe.id, api]);

  const sortedAttributes = [...attributes].sort((left, right) =>
    left.category.localeCompare(right.category)
      || left.name.localeCompare(right.name)
      || left.status.localeCompare(right.status),
  );

  const openOverride = (attribute: DietaryAttribute) => {
    setDialogError(null);
    setIsAdding(false);
    setSelected(attribute);
    setStatus(attribute.status);
    setReason('');
    setDialogOpen(true);
  };

  const openAdd = async () => {
    setDialogError(null);
    try {
      setReference(await api.getDietaryAttributeReference(recipe.franchiseId));
      setIsAdding(true);
      setSelected(null);
      setStatus(DietaryAttributeStatus.Suitable);
      setReason('');
      setDialogOpen(true);
    } catch (cause) {
      setReference([]);
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.dietaryLoadError'), severity: 'error' });
    }
  };

  // Reference rows are already scoped to the franchise's configured catalog by the API. Category is
  // a property of the definition itself (never independently chosen here) - only the categories this
  // flow supports adding are offered; Allergen/Intolerance are managed elsewhere.
  const addableReference = reference.filter(item => (ADDABLE_CATEGORIES as readonly DietaryCategory[]).includes(item.category));

  const statusOptionsForSelection = (attribute: DietaryAttribute | DietaryAttributeReference | null) =>
    CATEGORY_STATUS_OPTIONS[attribute?.category ?? ''] ?? ALL_STATUS_OPTIONS;

  const saveOverride = async () => {
    if (!selected || !reason.trim()) return;
    setSaving(true);
    try {
      await api.overrideDietaryAttribute(recipe.id, { attributeId: selected.id, status, reason: reason.trim() });
      const refreshed = await api.getDietaryAttributes(recipe.id);
      setAttributes(refreshed);
      onMetadataChange?.({ dietaryAttributes: refreshed });
      setDialogOpen(false);
      setFeedback({ message: isAdding ? t('feedback.dietaryAdded') : t('feedback.dietaryOverrideSaved'), severity: 'success' });
      setSelected(null);
      setReason('');
      setDialogError(null);
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : t('feedback.dietarySaveError'));
    } finally { setSaving(false); }
  };

  const requestRemove = (attribute: DietaryAttribute) => {
    setDeleteCandidate(attribute);
    setRemoveReason('');
  };

  const confirmRemove = async () => {
    if (!deleteCandidate || !removeReason.trim()) return;
    setIsRemoving(true);
    try {
      await api.clearDietaryAttributeOverride(recipe.id, deleteCandidate.id, removeReason.trim());
      const refreshed = await api.getDietaryAttributes(recipe.id);
      setAttributes(refreshed);
      onMetadataChange?.({ dietaryAttributes: refreshed });
      setFeedback({ message: t('feedback.dietaryOverrideRemoved'), severity: 'success' });
      setDeleteCandidate(null);
      setRemoveReason('');
    } catch (cause) {
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.dietaryRemoveError'), severity: 'error' });
    } finally { setIsRemoving(false); }
  };

  if (isLoading) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('loading')}</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}>
          <Typography variant="textXL">{t('dietary.title')}</Typography>
          {canOverrideDietary && <Button size="md"
            startIcon={<Add />}
            onClick={openAdd}>{t('dietary.add')}</Button>}
        </Stack>
        {attributes.length > 0 ? (
          <DietaryAttributeChips
            attributes={attributes}
            showName
            groupByCategory
            size="medium"
            spacing={1}
          />
        ) : <Typography color="text.secondary"
          variant="textSM">{t('dietary.noAttributes')}</Typography>}
      </Paper>

      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}>
          <Typography variant="textXL">
            {t('dietary.manage')}
          </Typography>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('category')}</TableCell>
                <TableCell>{t('dietary.currentValue')}</TableCell>
                <TableCell>{t('dietary.originalValue')}</TableCell>
                <TableCell>{t('source')}</TableCell>
                <TableCell>{t('actions.title')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAttributes.length === 0 ? (
                <RecipeV2EmptyState colSpan={6}>{t('dietary.noAttributes')}</RecipeV2EmptyState>
              ) : sortedAttributes.map(attribute => (
                <TableRow key={`${attribute.id}-${attribute.category}`}>
                  <TableCell>{attribute.name}</TableCell>
                  <TableCell>{displayValue(attribute.category)}</TableCell>
                  <TableCell>
                    <Chip label={displayValue(attribute.status)}
                      size="small"
                      color={statusChipColor(attribute.status)} />
                  </TableCell>
                  <TableCell>
                    {attribute.isOverridden && attribute.originalStatus ? (
                      <Chip label={displayValue(attribute.originalStatus)}
                        size="small"
                        variant="outlined"
                        color={statusChipColor(attribute.originalStatus)} />
                    ) : <Typography variant="textSM"
                      color="text.secondary">-</Typography>}
                  </TableCell>
                  <TableCell>{attribute.isOverridden ? t('allergens.sourceManual') : (attribute.source || '-')}</TableCell>
                  <TableCell>
                    {!canOverrideDietary ? '-' : (
                      <Stack direction="row"
                        spacing={0.5}
                        alignItems="center">
                        <Button size="md"
                          variant="secondary"
                          startIcon={<Edit fontSize="small" />}
                          onClick={() => openOverride(attribute)}>{t('dietary.override')}</Button>
                        {attribute.isOverridden && (
                          <IconButton size="small"
                            color="error"
                            aria-label={`Remove override for ${attribute.name}`}
                            onClick={() => requestRemove(attribute)}><Delete fontSize="small" /></IconButton>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{isAdding ? t('dietary.add') : t('dietary.overrideTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {dialogError && <Typography color="error">{dialogError}</Typography>}
            <Select value={selected?.id ?? ''}
              displayEmpty
              disabled={!isAdding && !!selected}
              onChange={event => {
                const list = isAdding ? addableReference : attributes;
                const match = list.find(item => item.id === Number(event.target.value)) ?? null;
                setSelected(match);
                setStatus(statusOptionsForSelection(match)[0]);
              }}>
              <MenuItem value=""
                disabled>{t('dietary.attributeName')}</MenuItem>
              {(isAdding ? addableReference : attributes).map(item => (
                <MenuItem key={item.id}
                  value={item.id}>{item.name}</MenuItem>
              ))}
            </Select>
            {isAdding && (
              <TextField label={t('category')}
                value={selected ? t(CATEGORY_LABEL_KEYS[selected.category]) : ''}
                disabled
                InputProps={{ readOnly: true }} />
            )}
            <Select value={status}
              onChange={event => setStatus(event.target.value as DietaryAttributeStatus)}>
              {statusOptionsForSelection(selected).map(option => (
                <MenuItem key={option}
                  value={option}>{t(STATUS_LABEL_KEYS[option])}</MenuItem>
              ))}
            </Select>
            <TextField label={t('dietary.overrideReason') as string}
              value={reason}
              onChange={event => setReason(event.target.value)}
              required
              multiline
              rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setDialogOpen(false)}>{t('actions.cancel')}</Button>
          <Button variant="primary"
            onClick={saveOverride}
            disabled={!selected || saving || !reason.trim()}>{isAdding ? t('dietary.addConfirm') : t('dietary.overrideConfirm')}</Button>
        </DialogActions>
      </Dialog>

      <RecipeOverrideRemovalDialog
        open={Boolean(deleteCandidate)}
        title={t('feedback.removeDietaryOverrideConfirmTitle')}
        message={t('feedback.removeDietaryOverrideConfirmMessage')}
        reason={removeReason}
        isProcessing={isRemoving}
        onReasonChange={setRemoveReason}
        onConfirm={confirmRemove}
        onCancel={() => {
          setDeleteCandidate(null);
          setRemoveReason('');
        }}
      />
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </Stack>
  );
};

export default DietaryTab;
